import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './ear-training-answer-button.module.css'

export type AnswerButtonState = 'idle' | 'correct' | 'incorrect' | 'dimmed'

export interface EarTrainingAnswerButtonProps {
  /** Stable identifier passed back via `onPick`. */
  pickValue: string
  /** Lexicon id whose `displayName` + level-resolved `tag` populate the button. */
  lexiconId: string
  /** Fallback long-name when the lexicon has no entry (or `theoryLevel === 'off'`). */
  fallbackLongName: string
  /** Fallback short-name. */
  fallbackShortName: string
  state: AnswerButtonState
  onPick: (pickValue: string) => void
}

/**
 * Shared answer-button primitive used by both Interval-mode and Chord-quality-
 * mode question panels. Mirrors `FunctionQuizAnswerCard` — state drives
 * disabled-ness + visual treatment (idle interactive; correct/incorrect/dimmed
 * post-answer). The long name comes from the lexicon's `displayName`; the
 * short tag from the level-resolved `tag`.
 */
export const EarTrainingAnswerButton: FC<EarTrainingAnswerButtonProps> = props => {
  const { pickValue, lexiconId, fallbackLongName, fallbackShortName, state, onPick } = props
  const copy = useTheoryCopy(lexiconId)
  const longName = copy?.displayName ?? fallbackLongName
  const shortTag = copy?.tag ?? fallbackShortName
  const isInteractive = state === 'idle'
  const stateClass = styles[state] ?? ''
  return (
    <button
      type="button"
      className={`${styles.button} ${stateClass}`}
      disabled={!isInteractive}
      aria-pressed={state === 'correct'}
      onClick={() => onPick(pickValue)}
    >
      <span className={styles.longName}>{longName}</span>
      <span className={styles.shortTag}>{shortTag}</span>
    </button>
  )
}
